import { useEffect, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { RadioButton } from '@/src/elements/radio/RadioButton'
import { useCommunityQuery } from '@/src/features/community/services/useCommunityQuery'
import { useJoinCommunityMutation } from '@/src/features/community/services/useJoinCommunity'
import { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { useFraternityOnboardingMutation } from '@/src/features/fraternity/queries/useFraternityOnboardingMutation'
import { FraternityOnboardingWrapper } from '@/src/features/fraternity/ui/components/FraternityOnboardingWrapper'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useTranslate } from '@/src/lang/useTranslate'

type TFraternityReadyScreenProps = TIdSlugParam & TSearchParamsParam

export const FraternityReadyScreen = ({ idSlug, searchParams }: TFraternityReadyScreenProps): ReactNode => {
  const [isEager, setIsEager] = useState<boolean | null>(null)
  const { t } = useTranslate()
  const [id] = parseIdDash(idSlug)
  const { data: community } = useCommunityQuery(id)

  const joinSteps = useCommunityJoinSteps({ idSlug, searchParams, stepName: 'ready' })
  const { goToNextStep } = joinSteps

  const {
    join,
    isPending: isJoinPending,
    isSuccess: isJoinSuccess,
    isError: isJoinError,
    error: joinError,
  } = useJoinCommunityMutation(id, searchParams)

  const { mutateAsync, isPending: isSaving } = useFraternityOnboardingMutation()

  useEffect(() => {
    if (community && !community.hasJoined && !isJoinPending && !isJoinSuccess && !isJoinError) {
      join('ongoing')
    }
  }, [community, isJoinError, isJoinPending, isJoinSuccess, join])

  const handleValidate = async () => {
    if (isEager == null) return
    await mutateAsync({ is_eager: isEager })
    goToNextStep()
  }

  if (isJoinError) {
    return (
      <ErrorMessage
        error={joinError}
        defaultErrorKey="common.error.unknown"
        button={[{ children: t('tab.error.try-again'), onPress: () => join('ongoing') }]}
      />
    )
  }

  return (
    <FraternityOnboardingWrapper
      joinSteps={joinSteps}
      title={<FormattedMessage id="fraternity.ready.title" />}
      subtitle={<FormattedMessage id="fraternity.ready.subtitle" />}
      footer={
        <Button
          colors="primary"
          variant="solid"
          fullWidth
          isLoading={isSaving}
          isDisabled={isEager == null}
          onPress={handleValidate}
        >
          <FormattedMessage id="word.continue" />
        </Button>
      }
    >
      <View className="gap-3">
        <RadioButton
          selected={isEager === true}
          label={t('fraternity.ready.eager-yes')}
          onPress={() => setIsEager(true)}
        />
        <RadioButton
          selected={isEager === false}
          label={t('fraternity.ready.eager-no')}
          onPress={() => setIsEager(false)}
        />
      </View>
    </FraternityOnboardingWrapper>
  )
}
